import React, { useEffect, useRef, useState } from 'react';
import { Plane, Heart, Copy, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { useLanguage } from '../i18n';

const GiftSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iban = "IT56H0366901600155665913516";
  const intestatario = "Mirko Orlando";

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(iban);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Format IBAN for display
  const formatIban = (iban) => {
    return iban.match(/.{1,4}/g)?.join(' ') || iban;
  };

  return (
    <section 
      ref={sectionRef}
      className="py-28 md:py-40 bg-background relative overflow-hidden"
      id="regalo"
    >
      {/* Decorative elements */}
      <div className="absolute top-1/4 left-0 w-48 h-48 bg-lavender/8 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-0 w-48 h-48 bg-blush/25 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-pale-blue/10 rounded-full blur-3xl" />
      
      {/* Floral pattern */}
      <div className="absolute inset-0 floral-pattern opacity-25" />
      
      {/* Floating sparkles */}
      <div className="absolute top-1/3 left-1/3 text-gold/30 text-xl animate-twinkle">✦</div>
      <div className="absolute bottom-1/3 right-1/3 text-gold/25 text-lg animate-twinkle delay-300">✦</div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="w-24 h-24 rounded-full bg-lavender/10 border-2 border-lavender/25 flex items-center justify-center mx-auto mb-10">
            <Plane className="w-10 h-10 text-lavender" />
          </div>
          <p className="font-display text-sm uppercase tracking-[0.5em] text-gold mb-5">
            {t('gift.eyebrow')}
          </p>
          <h2 className="font-script text-5xl md:text-6xl lg:text-7xl text-lavender mb-12">
            {t('gift.title')}
          </h2>
          
          {/* Personal message */}
          <div className="max-w-2xl mx-auto space-y-6">
            <p className="font-elegant text-2xl md:text-3xl text-foreground leading-relaxed italic">
              {t('gift.p1')}
            </p>
            <p className="font-elegant text-2xl md:text-3xl text-foreground leading-relaxed italic">
              {t('gift.p2')}<span className="text-lavender">{t('gift.p2highlight')}</span>
            </p>
            <div className="flex items-center justify-center gap-4 my-8">
              <span className="w-12 h-px bg-gold/40" />
              <span className="text-lavender/50">❦</span>
              <span className="w-12 h-px bg-gold/40" />
            </div>
            <p className="font-elegant text-xl text-muted-foreground leading-relaxed">
              {t('gift.p3')}
            </p>
          </div>
        </div>

        {/* Gift Card */}
        <Card className={`max-w-2xl mx-auto bg-card/95 backdrop-blur-sm border-2 border-gold/30 shadow-elegant transition-all duration-1000 delay-300 gold-frame ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <CardContent className="p-12 md:p-16 relative">
            {/* Inner decorative border */}
            <div className="absolute inset-5 border border-lavender/20 rounded-lg pointer-events-none" />
            
            {/* Decorative top border */}
            <div className="flex items-center justify-center gap-5 mb-12">
              <span className="w-24 h-px bg-gradient-to-r from-transparent to-gold/50" />
              <Heart className="w-7 h-7 text-gold" fill="currentColor" />
              <span className="w-24 h-px bg-gradient-to-l from-transparent to-gold/50" />
            </div>
            
            {/* IBAN Details */}
            <div className="text-center space-y-10">
              <div>
                <p className="font-display text-xs uppercase tracking-[0.4em] text-muted-foreground mb-4">
                  {t('gift.holder')}
                </p>
                <p className="font-script text-3xl md:text-4xl text-lavender">
                  {intestatario}
                </p>
              </div>
              
              <div className="relative">
                <p className="font-display text-xs uppercase tracking-[0.4em] text-muted-foreground mb-5">
                  IBAN
                </p>
                <div className="bg-lavender/5 rounded-xl p-6 md:p-8 border-2 border-lavender/15 relative">
                  {/* Corner decorations */}
                  <div className="absolute top-3 left-3 w-5 h-5 border-l-2 border-t-2 border-gold/30" />
                  <div className="absolute bottom-3 right-3 w-5 h-5 border-r-2 border-b-2 border-gold/30" />
                  
                  <p className="font-display text-xs sm:text-base md:text-lg text-foreground tracking-widest whitespace-nowrap">
                    {formatIban(iban)}
                  </p>
                </div>
              </div>
              
              {/* Copy Button */}
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="mt-8 gap-3 border-2 border-lavender/40 text-foreground hover:bg-lavender/10 hover:border-lavender/60 transition-all duration-300 px-10 py-6 rounded-lg"
                data-testid="copy-iban-button"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5 text-lavender" />
                    <span className="font-display text-sm uppercase tracking-wider">{t('gift.copied')}</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    <span className="font-display text-sm uppercase tracking-wider">{t('gift.copy')}</span>
                  </>
                )}
              </Button>
            </div>
            
            {/* Decorative bottom */}
            <div className="flex items-center justify-center gap-4 mt-14">
              <span className="w-12 h-px bg-lavender/25" />
              <span className="text-gold/50">✿</span>
              <span className="text-lavender/40">❦</span>
              <span className="text-gold/50">✿</span>
              <span className="w-12 h-px bg-lavender/25" />
            </div>
          </CardContent>
        </Card>

        {/* Thank you message */}
        <p className={`text-center font-elegant text-xl text-muted-foreground italic mt-14 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          {t('gift.thanks')}
        </p>
      </div>
    </section>
  );
};

export default GiftSection;
